import type { SetStateAction } from 'react';

import { atom, type PrimitiveAtom, type WritableAtom } from 'jotai';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports -- Jotai RESET is a `unique symbol`; atom generics need `typeof RESET`, which requires a value binding
import { atomWithReset, RESET } from 'jotai/utils';

import {
	EMPTY_TRACE_BUFFERS,
	readTraceEvent,
	type BenchmarkInvocationDelta,
} from './api/traceRecords';
import type {
	BenchmarkArm,
	BenchmarkArmResult,
	BenchmarkErrorEventData,
	BenchmarkEvent,
	BenchmarkRequestFailure,
	BenchmarkResultEventData,
	BenchmarkResultPayload,
	BenchmarkToolSummaryEntry,
} from './api/types';
import type { BenchmarkErrorReport } from './errorReport';
import { benchmarkTranscriptAtom, createTranscriptState } from './transcriptState';
import type {
	BenchmarkArmState,
	BenchmarkConnectionStatus,
	BenchmarkInvocationStatus,
	BenchmarkRunKey,
	BenchmarkRunOutcome,
	BenchmarkRunState,
} from './types';

const createInitialArmState = (): BenchmarkArmState => ({
	status: 'waiting',
	model: null,
	startedAtMs: null,
	durationMs: null,
	usage: null,
	startedToolCount: 0,
	failedToolCount: 0,
	invocations: [],
	transcriptTruncated: false,
});

export const createInitialBenchmarkRunState = (): BenchmarkRunState => ({
	phase: 'provisioning',
	arms: { control: createInitialArmState(), twg: createInitialArmState() },
	lastSequence: 0,
	lastEventAtMs: null,
	result: null,
	error: null,
	traceBuffers: EMPTY_TRACE_BUFFERS,
});

const parseEventTime = (timestamp: string): number | null => {
	const parsed = Date.parse(timestamp);

	return Number.isNaN(parsed) ? null : parsed;
};

const withArm = (
	state: BenchmarkRunState,
	arm: BenchmarkArm,
	update: (armState: BenchmarkArmState) => BenchmarkArmState,
): BenchmarkRunState => ({
	...state,
	arms: { ...state.arms, [arm]: update(state.arms[arm]) },
});

/**
 * Resolves rows still marked running once their arm has finished. Nothing more
 * is coming for them, so leaving them spinning forever would misreport a
 * settled arm as still working.
 */
const settleOrphanInvocations = (armState: BenchmarkArmState): BenchmarkArmState => ({
	...armState,
	invocations: armState.invocations.map((invocation) =>
		invocation.status === 'running' ? { ...invocation, status: 'unknown' } : invocation,
	),
});

const applyInvocationDelta = (
	armState: BenchmarkArmState,
	delta: BenchmarkInvocationDelta,
	eventAtMs: number | null,
): BenchmarkArmState => {
	const index = armState.invocations.findIndex(
		(invocation) => invocation.invocationId === delta.invocationId,
	);

	if (delta.phase === 'started') {
		// A repeated start for the same id carries nothing new.
		if (index !== -1) {
			return armState;
		}

		return {
			...armState,
			invocations: [
				...armState.invocations,
				{
					invocationId: delta.invocationId,
					kind: delta.kind,
					surface: delta.surface,
					label: delta.label,
					status: 'running',
					startedAtMs: eventAtMs,
					durationMs: null,
					exitCode: null,
				},
			],
		};
	}

	const status: BenchmarkInvocationStatus = delta.failed ? 'failed' : 'completed';

	// A completion with no matching start means the start was dropped upstream,
	// so the row is created from the completion alone and has no duration.
	if (index === -1) {
		return {
			...armState,
			invocations: [
				...armState.invocations,
				{
					invocationId: delta.invocationId,
					kind: delta.kind,
					surface: delta.surface,
					label: delta.label,
					status,
					startedAtMs: null,
					durationMs: null,
					exitCode: delta.exitCode,
				},
			],
		};
	}

	const existing = armState.invocations[index];
	const invocations = [...armState.invocations];

	invocations[index] = {
		...existing,
		status,
		label: existing.label || delta.label,
		durationMs:
			eventAtMs !== null && existing.startedAtMs !== null
				? eventAtMs - existing.startedAtMs
				: existing.durationMs,
		exitCode: delta.exitCode,
	};

	return { ...armState, invocations };
};

const settleEveryArm = (state: BenchmarkRunState): BenchmarkRunState => ({
	...state,
	arms: {
		control: settleOrphanInvocations(state.arms.control),
		twg: settleOrphanInvocations(state.arms.twg),
	},
});

const markEveryTranscriptTruncated = (state: BenchmarkRunState): BenchmarkRunState => ({
	...state,
	arms: {
		control: { ...state.arms.control, transcriptTruncated: true },
		twg: { ...state.arms.twg, transcriptTruncated: true },
	},
});

/**
 * Folds one event into the run's view model.
 *
 * Pure, and deliberately so: replaying the same events in the same order
 * always produces the same state, which is what lets the stream layer replay a
 * journal from the start after a reload, and lets tests and stories build any
 * state by feeding it a captured run.
 *
 * The division of labour between two overlapping event types is the crux:
 *
 * - **`trace` builds the rows.** It carries the executed command text and the
 *   Codex `item.id`, which is the only reliable way to pair a start with its
 *   completion. It is also lossy, so the row list is precise but incomplete.
 * - **`tool` supplies the counts.** It has no invocation id, so its events
 *   cannot be paired, but it is emitted for every invocation. That makes it
 *   the honest source for "how many calls so far", which is why the footer
 *   count can legitimately exceed the number of visible rows.
 */
export const applyBenchmarkEvent = (
	state: BenchmarkRunState,
	event: BenchmarkEvent,
): BenchmarkRunState => {
	// A rollover marker reuses the current sequence as its id and carries
	// nothing to render, so it must not touch the cursor.
	if (event.name === 'reconnect') {
		return state;
	}

	const eventAtMs = parseEventTime(event.data.timestamp);
	const base: BenchmarkRunState = {
		...state,
		lastSequence: Math.max(state.lastSequence, event.data.sequence),
		lastEventAtMs: eventAtMs ?? state.lastEventAtMs,
	};

	switch (event.name) {
		case 'transcript':
			return base;

		case 'heartbeat':
			// Liveness only. The watchdog reads it; nothing renders it.
			return base;

		case 'lifecycle':
			return base.phase === 'complete' || base.phase === 'failed'
				? base
				: { ...base, phase: event.data.phase };

		case 'arm': {
			const { arm, status, model, durationMs, usage } = event.data;

			return withArm(base, arm, (armState) =>
				status === 'started'
					? {
							...armState,
							status: 'running',
							model,
							startedAtMs: eventAtMs ?? armState.startedAtMs,
						}
					: settleOrphanInvocations({
							...armState,
							status: status === 'completed' ? 'completed' : 'failed',
							model,
							durationMs: durationMs ?? armState.durationMs,
							usage: usage ?? armState.usage,
						}),
			);
		}

		case 'tool': {
			const { arm, status } = event.data;

			return withArm(base, arm, (armState) => ({
				...armState,
				startedToolCount: armState.startedToolCount + (status === 'started' ? 1 : 0),
				failedToolCount: armState.failedToolCount + (status === 'failed' ? 1 : 0),
			}));
		}

		case 'trace': {
			const { buffers, outcome } = readTraceEvent(base.traceBuffers, event.data);
			const next: BenchmarkRunState = { ...base, traceBuffers: buffers };
			const { arm } = event.data;

			if (outcome.kind === 'captureIncomplete') {
				return arm
					? withArm(next, arm, (armState) => ({ ...armState, transcriptTruncated: true }))
					: markEveryTranscriptTruncated(next);
			}

			// An invocation with no arm cannot be attributed to a column.
			return outcome.kind === 'invocation' && arm
				? withArm(next, arm, (armState) => applyInvocationDelta(armState, outcome.delta, eventAtMs))
				: next;
		}

		case 'diagnostic':
			// Structured per-arm telemetry for an expert panel. This screen does
			// not render it, and the arm summary it repeats is already covered by
			// the `arm` event.
			return base;

		case 'result':
			return { ...settleEveryArm(base), phase: 'complete', result: event.data };

		case 'error':
			return { ...settleEveryArm(base), phase: 'failed', error: event.data };
	}
};

/** Replays a run from the beginning. Used by tests and stories. */
export const replayBenchmarkEvents = (
	events: readonly BenchmarkEvent[],
	initial: BenchmarkRunState = createInitialBenchmarkRunState(),
): BenchmarkRunState => events.reduce(applyBenchmarkEvent, initial);

const isFailedToolEntry = (tool: BenchmarkToolSummaryEntry): boolean =>
	tool.status === 'failed' || tool.status === 'stalled';

/**
 * Rebuilds one arm from its result payload, in whichever shape it arrived.
 *
 * A counted payload carries one row per call plus the sandbox's own totals,
 * which hold past the 100-row cap on `toolSummary`, so the figures come from
 * those rather than from the rows. An older one carries a log of tool
 * *events* instead, one `started` plus one outcome each: a verified run
 * reported 46 entries for 23 calls, so its count comes from the `started`
 * rows and its list from the outcomes, the only reading that does not double
 * every call. `toolCallCount` tells the two apart.
 *
 * A stalled call counts as an error, as it does everywhere else a figure is
 * drawn. It is the same outcome to a reader as a call that failed outright,
 * and counting only `failed` is what let an arm whose calls all hung rebuild
 * as though nothing had gone wrong.
 */
const armStateFromResult = (armResult: BenchmarkArmResult): BenchmarkArmState => {
	const counted = typeof armResult.toolCallCount === 'number';
	const rows = counted
		? armResult.toolSummary
		: armResult.toolSummary.filter((tool) => tool.status !== 'started');

	return {
		status: armResult.status,
		model: armResult.model,
		startedAtMs: null,
		durationMs: armResult.durationMs,
		usage: armResult.usage,
		startedToolCount: counted
			? (armResult.toolCallCount ?? 0)
			: armResult.toolSummary.filter((tool) => tool.status === 'started').length,
		failedToolCount: counted
			? (armResult.failedCount ?? 0) + (armResult.stalledCount ?? 0)
			: rows.filter(isFailedToolEntry).length,
		invocations: rows.map((tool, index) => ({
			// The older shape carries no call id, so one is synthesised from the
			// position. Rows rebuilt that way are display-only.
			invocationId: tool.callId ?? `summary-${index}`,
			kind: tool.surface === 'mcp' ? 'mcp' : 'command',
			surface: tool.surface,
			label: tool.name,
			status: isFailedToolEntry(tool) ? 'failed' : 'completed',
			startedAtMs: null,
			durationMs: tool.durationMs ?? null,
			exitCode: null,
		})),
		transcriptTruncated: false,
	};
};

/**
 * Rebuilds a finished run's view model from its result payload.
 *
 * Used when a reload happens after the run has finished, where re-attaching is
 * pointless or impossible: the journal survives only ten minutes and nothing
 * is stored server-side. It is a reconstruction rather than a replay, so two
 * things are weaker than the live version. Per-invocation timing and command
 * text are gone, because `toolSummary` carries neither, and the counts come
 * from a list the service truncates at 100 entries per arm, so a very busy arm
 * will under-report.
 */
export const benchmarkRunStateFromResult = (
	result: BenchmarkResultEventData,
): BenchmarkRunState => ({
	...createInitialBenchmarkRunState(),
	phase: 'complete',
	arms: {
		control: armStateFromResult(result.arms.control),
		twg: armStateFromResult(result.arms.twg),
	},
	result,
});

/**
 * A stored report's payload, dressed as the event frame it never arrived on.
 *
 * `GET /gateway/api/twg-api-v2/{cloudId}/v1/microsite/benchmark/runs/{runId}/report`
 * returns the payload without an envelope, because the service strips one
 * before storing it. The run id is known from the address and the time from
 * the report's own completion, so only `sequence` has no source: it is zero
 * because there is no frame, and nothing reads it back. The sequence that
 * matters for resuming a stream is tracked on the run state, not the payload.
 */
export const benchmarkResultFromReport = (
	runId: string,
	result: BenchmarkResultPayload,
	completedAt: string | null,
): BenchmarkResultEventData => ({
	...result,
	runId,
	sequence: 0,
	timestamp: completedAt ?? new Date().toISOString(),
});

export const benchmarkRunStateFromError = (error: BenchmarkErrorEventData): BenchmarkRunState => ({
	...createInitialBenchmarkRunState(),
	phase: 'failed',
	error,
});

export const benchmarkRunStateAtom: PrimitiveAtom<BenchmarkRunState> & {
	init: BenchmarkRunState;
} = atom<BenchmarkRunState>(createInitialBenchmarkRunState());

/**
 * How the run in hand ended, for as long as this page is mounted.
 *
 * No longer the retention story, which is what it used to be: benchmark output
 * is not stored client-side any more, and
 * `GET /gateway/api/twg-api-v2/{cloudId}/v1/microsite/benchmark/runs/{runId}/report`
 * outlives both the ten-minute event journal and the tab. This is now only a
 * within-session record of a terminal run, so the comparison and the
 * transcript can be drawn without asking for the report a second time. A
 * reload refills it from the report rather than from storage.
 */
export const benchmarkRunOutcomeAtom: WritableAtom<
	BenchmarkRunOutcome | null,
	[SetStateAction<BenchmarkRunOutcome | null> | typeof RESET],
	void
> = atomWithReset<BenchmarkRunOutcome | null>(null);

export const benchmarkConnectionStatusAtom: PrimitiveAtom<BenchmarkConnectionStatus> & {
	init: BenchmarkConnectionStatus;
} = atom<BenchmarkConnectionStatus>('idle');

/**
 * Whether this run's final answers have been shown on the run page.
 *
 * They are blurred until they are, because the blind review comes next and the terminals say
 * which setup wrote which answer. Held beside the run rather than on the review record: the
 * run is over before there is a review to write to, and this is about what the page shows.
 */
export const benchmarkFinalAnswerShownAtom: PrimitiveAtom<boolean> & { init: boolean } =
	atom<boolean>(false);

/** A failed start or cancel, held so the view can explain itself. */
export const benchmarkRequestFailureAtom: PrimitiveAtom<BenchmarkRequestFailure | null> & {
	init: BenchmarkRequestFailure | null;
} = atom<BenchmarkRequestFailure | null>(null);

/**
 * A reconnect that keeps failing raises a report each time it gives up, so the
 * list is capped: a stack of flags tall enough to cover the run is worse than
 * no flag at all, and the oldest report is the least useful one to escalate.
 */
const MAX_BENCHMARK_ERROR_REPORTS = 3;

/**
 * Failures worth escalating, newest first.
 *
 * Newest first because the flag group treats the first entry as the topmost
 * one and the only dismissable one, and the failure someone is reacting to is
 * the one that just happened.
 *
 * Deliberately not persisted. A trace id is only worth anything while the
 * person still has the failure in front of them, so rehydrating one after a
 * reload would put a stale flag over a working page.
 */
export const benchmarkErrorReportsAtom: PrimitiveAtom<readonly BenchmarkErrorReport[]> & {
	init: readonly BenchmarkErrorReport[];
} = atom<readonly BenchmarkErrorReport[]>([]);

export const pushBenchmarkErrorReportAtom: WritableAtom<null, [BenchmarkErrorReport], void> = atom(
	null,
	(get, set, report: BenchmarkErrorReport) => {
		set(
			benchmarkErrorReportsAtom,
			[report, ...get(benchmarkErrorReportsAtom)].slice(0, MAX_BENCHMARK_ERROR_REPORTS),
		);
	},
);

export const dismissBenchmarkErrorReportAtom: WritableAtom<null, [string | number], void> = atom(
	null,
	(get, set, id: string | number) => {
		set(
			benchmarkErrorReportsAtom,
			get(benchmarkErrorReportsAtom).filter((report) => report.id !== id),
		);
	},
);

/**
 * Clears the state associated with the previously addressed run.
 *
 * Local only: a run that has not finished carries on executing in its sandbox,
 * so a caller releasing a live run rather than cancelling it is choosing to
 * leave it unwatched. Reviews are stored separately by run ID and survive.
 *
 * Shared rather than written out at each call site so route changes cannot
 * leave one run's timeline, failures or transcript under another run's URL.
 * A caller revalidating the same run may preserve its matching terminal
 * outcome, which remains the report fallback until server-side capture lands.
 */
export const releaseBenchmarkRunAtom: WritableAtom<
	null,
	[preserveOutcomeFor?: BenchmarkRunKey],
	void
> = atom(null, (get, set, preserveOutcomeFor) => {
	const outcome = get(benchmarkRunOutcomeAtom);
	const shouldPreserveOutcome =
		preserveOutcomeFor !== undefined &&
		outcome?.cloudId === preserveOutcomeFor.cloudId &&
		outcome.runId === preserveOutcomeFor.runId;

	if (!shouldPreserveOutcome) {
		set(benchmarkRunOutcomeAtom, RESET);
	}
	set(benchmarkRunStateAtom, createInitialBenchmarkRunState());
	set(benchmarkTranscriptAtom, createTranscriptState());
	set(benchmarkConnectionStatusAtom, 'idle');
	set(benchmarkRequestFailureAtom, null);
	set(benchmarkErrorReportsAtom, []);
});
